import { datadogLogs } from '@datadog/browser-logs';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import proofApi from 'src/features/proofs/api';
import { cleanFileName, configVars } from 'src/helpers';
import { downloadDocument } from 'src/helpers/downloadUtils';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';
import ViewSDKClient from 'src/pdfViewer/ViewSDKClient';
import { FeatureFlagKey } from 'src/ts/enums';
import { IProof } from 'src/ts/interfaces';
import { IListCheckCandidate, IListCheckEntry } from 'src/ts/interfaces/listCheck';
import {
  ListCheckComparison,
  listCheckComparison,
  ListCheckComparisonRow,
  ListCheckComparisonStatus
} from '../../helpers/listCheckHelpers';
import {
  ListCheckCompare,
  ListCheckCompareChip,
  ListCheckCompareClosest,
  ListCheckCompareColumn,
  ListCheckCompareHead,
  ListCheckCompareMark,
  ListCheckCompareMatchPercent,
  ListCheckCompareMuted,
  ListCheckComparePill,
  ListCheckCompareRow,
  ListCheckCompareRule,
  ListCheckCompareSame,
  ListCheckCompareSummary,
  ListCheckCompareTitle,
  ListCheckCompareValue,
  ListCheckPane,
  ListCheckPaneFrame,
  ListCheckPaneDownload,
  ListCheckPaneLabel,
  ListCheckPanes,
  ListCheckPaneStatus,
  ListCheckPaneViewer,
  ListCheckPickTag,
  ListCheckRowLink,
  ListCheckRowRef,
  ListCheckTile,
  ListCheckTileSub,
  ListCheckTileTop
} from '../../styles';
import { listCheckCopy } from './listCheckCopy';
import CandidateNotes from './CandidateNotes';

const VIEWER_CONFIG = {
  embedMode: 'IN_LINE',
  showPageControls: false,
  showFullScreen: false,
  showDownloadPDF: false,
  showPrintPDF: false
};

type ViewerStatus = 'loading' | 'ready' | 'error' | 'unavailable';

interface DocumentPaneProps {
  containerId: string;
  title: string;
  titleAside?: ReactNode;
  url: string | null | undefined;
  fileName: string;
  fileId: string;
  unavailableText: string;
  downloadName?: string;
  onError: (error: unknown) => void;
}

export const DocumentPane: FC<DocumentPaneProps> = ({
  containerId,
  title,
  titleAside,
  url,
  fileName,
  fileId,
  unavailableText,
  downloadName,
  onError
}) => {
  const [status, setStatus] = useState<ViewerStatus>('loading');

  useEffect(() => {
    if (url === undefined) {
      setStatus('loading');
      return undefined;
    }
    if (!url) {
      setStatus('unavailable');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    const client = new ViewSDKClient();

    const renderDocument = async () => {
      try {
        await client.ready();
        if (cancelled) return;
        await client.previewFile(
          containerId,
          VIEWER_CONFIG,
          url,
          fileName,
          fileId,
          configVars.adobe.pdf_token
        );
        if (cancelled) return;
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        setStatus('error');
        onError(error);
      }
    };

    renderDocument();

    return () => {
      cancelled = true;
    };
  }, [containerId, url, fileName, fileId, onError]);

  return (
    <ListCheckPane>
      <ListCheckPaneLabel>
        {title}
        {titleAside}
        {downloadName && url && (
          <ListCheckPaneDownload
            type="button"
            onClick={() => downloadDocument(url, downloadName, fileId)}
          >
            {listCheckCopy.download}
          </ListCheckPaneDownload>
        )}
      </ListCheckPaneLabel>
      <ListCheckPaneFrame $empty={status === 'unavailable'}>
        <ListCheckPaneViewer id={containerId} data-testid={containerId} />
        {status === 'loading' && (
          <ListCheckPaneStatus role="status">Loading document…</ListCheckPaneStatus>
        )}
        {status === 'unavailable' && (
          <ListCheckPaneStatus role="status">{unavailableText}</ListCheckPaneStatus>
        )}
        {status === 'error' && (
          <ListCheckPaneStatus role="status">
            This document could not be loaded.
          </ListCheckPaneStatus>
        )}
      </ListCheckPaneFrame>
    </ListCheckPane>
  );
};

type CompareTone = 'amber' | 'red' | 'green' | 'plain';

const statusTone = (isPositive: boolean, status: ListCheckComparisonStatus): CompareTone => {
  if (status === 'differs' || status === 'check' || status === 'close') return 'amber';
  if (status === 'match' || status === 'found') {
    return isPositive ? 'green' : 'red';
  }
  return 'plain';
};

const countOf = (comparison: ListCheckComparison, status: ListCheckComparisonStatus): number =>
  comparison.rows.filter((row) => row.status === status).length;

const summary = (comparison: ListCheckComparison): string => {
  const total = comparison.compared;
  if (comparison.kind === 'profile')
    return listCheckCopy.differCount(comparison.rows.length, total);
  if (comparison.kind === 'registry') return listCheckCopy.checkProvider;
  const close = countOf(comparison, 'close');
  const counted =
    comparison.kind === 'rule'
      ? listCheckCopy.matchCount(countOf(comparison, 'match'), total)
      : listCheckCopy.foundCount(countOf(comparison, 'found'), total);
  return close ? `${counted}${listCheckCopy.closeCount(close)}` : counted;
};

const Chips: FC<{ values: string[]; other: string[]; tone: 'document' | 'list' }> = ({
  values,
  other,
  tone
}) => (
  <>
    {values.map((value, i) => (
      <ListCheckCompareChip key={`${value}-${i}`} $tone={other.includes(value) ? 'shared' : tone}>
        {value}
      </ListCheckCompareChip>
    ))}
  </>
);

const Marked: FC<{ line: string; value: string; tone: CompareTone }> = ({ line, value, tone }) => {
  const at = line.toLowerCase().indexOf(value.toLowerCase());
  if (at < 0) return <>{line}</>;
  return (
    <>
      {line.slice(0, at)}
      <ListCheckCompareMark $tone={tone}>{line.slice(at, at + value.length)}</ListCheckCompareMark>
      {line.slice(at + value.length)}
    </>
  );
};

const DocumentCell: FC<{ row: ListCheckComparisonRow; tone: CompareTone }> = ({ row, tone }) => {
  if (row.status === 'notOnList') return <ListCheckCompareMuted>—</ListCheckCompareMuted>;
  if (row.documentValue === null) {
    return <ListCheckCompareMuted>{listCheckCopy.notRead}</ListCheckCompareMuted>;
  }
  if (row.status === 'differs' && row.documentValue === '') {
    return <ListCheckCompareMuted>{listCheckCopy.blank}</ListCheckCompareMuted>;
  }
  if (row.status === 'notFound') {
    return (
      <>
        <ListCheckCompareMuted>—</ListCheckCompareMuted>
        <ListCheckCompareClosest>
          {listCheckCopy.closest(row.documentValue)}
        </ListCheckCompareClosest>
      </>
    );
  }
  if (row.status === 'found') {
    return <Marked line={row.documentValue} value={row.listValue} tone={tone} />;
  }
  if (row.documentList && row.list) {
    return <Chips values={row.documentList} other={row.list} tone="document" />;
  }
  return <>{row.documentValue}</>;
};

const ListCell: FC<{ row: ListCheckComparisonRow; kind: ListCheckComparison['kind'] }> = ({
  row,
  kind
}) => {
  if (row.status === 'notOnList') {
    return <ListCheckCompareMuted>{listCheckCopy.notOnList}</ListCheckCompareMuted>;
  }
  if (kind === 'rule') return <ListCheckCompareRule>{row.listValue}</ListCheckCompareRule>;
  if (kind === 'profile' && row.listValue === '') {
    return <ListCheckCompareMuted>{listCheckCopy.blank}</ListCheckCompareMuted>;
  }
  if (row.documentList && row.list) {
    return <Chips values={row.list} other={row.documentList} tone="list" />;
  }
  return <>{row.listValue}</>;
};

const ComparisonTable: FC<{
  comparison: ListCheckComparison;
  position: number;
  isPositive: boolean;
}> = ({ comparison, position, isPositive }) => {
  const { kind, rows, matchingColumns, compared } = comparison;
  return (
    <>
      <ListCheckCompare data-testid="list-check-comparison" data-kind={kind}>
        <ListCheckCompareHead>
          <ListCheckCompareTitle>{listCheckCopy.comparisonTitle[kind]}</ListCheckCompareTitle>
          <ListCheckCompareSummary data-testid="list-check-comparison-summary">
            {summary(comparison)}
          </ListCheckCompareSummary>
          {kind === 'profile' && (
            <ListCheckCompareMatchPercent data-testid="list-check-comparison-match-percent">
              {listCheckCopy.matchPercent(Math.round((matchingColumns.length / compared) * 100))}
            </ListCheckCompareMatchPercent>
          )}
        </ListCheckCompareHead>
        {rows.length > 0 && (
          <ListCheckCompareRow $head>
            <span />
            <span>{listCheckCopy.thisDocument}</span>
            <span>{kind === 'rule' ? listCheckCopy.rule : listCheckCopy.candidate(position)}</span>
            <span />
          </ListCheckCompareRow>
        )}
        {rows.map((row) => {
          const tone = statusTone(isPositive, row.status);
          return (
            <ListCheckCompareRow
              key={row.column}
              $tone={tone}
              data-testid="list-check-comparison-row"
              data-status={row.status}
            >
              <ListCheckCompareColumn>{row.column}</ListCheckCompareColumn>
              <ListCheckCompareValue>
                <DocumentCell row={row} tone={tone} />
              </ListCheckCompareValue>
              <ListCheckCompareValue $list>
                <ListCell row={row} kind={kind} />
              </ListCheckCompareValue>
              {row.status === 'unscored' || row.status === 'notOnList' ? (
                <span />
              ) : (
                <ListCheckComparePill $tone={tone}>
                  {listCheckCopy.status[row.status]}
                </ListCheckComparePill>
              )}
            </ListCheckCompareRow>
          );
        })}
      </ListCheckCompare>
      {matchingColumns.length > 0 && (
        <ListCheckCompareSame data-testid="list-check-comparison-same">
          {listCheckCopy.sameColumns(matchingColumns)}
        </ListCheckCompareSame>
      )}
    </>
  );
};

export const RowReference: FC<{ rowRef: string; rowUrl?: string | null }> = ({
  rowRef,
  rowUrl
}) => {
  const { copy } = useCopyToClipboard();
  return rowUrl ? (
    <ListCheckRowLink
      id="list-check-open-row"
      href={rowUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={listCheckCopy.openRow}
    >
      {rowRef}
    </ListCheckRowLink>
  ) : (
    <ListCheckRowRef
      type="button"
      title={listCheckCopy.copyId}
      aria-label={`${listCheckCopy.copyId} ${rowRef}`}
      onClick={() => copy(rowRef, listCheckCopy.idCopied(rowRef))}
    >
      {rowRef}
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        aria-hidden="true"
      >
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
        <path d="M8.5 1.5h-6a1 1 0 0 0-1 1v6" />
      </svg>
    </ListCheckRowRef>
  );
};

export const useReviewedUrl = (currentProof?: IProof): string | null | undefined => {
  const proofId = currentProof?.id;
  const storedUrl = currentProof?.file ?? null;
  const [reviewedUrl, setReviewedUrl] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!proofId) {
      setReviewedUrl(null);
      return undefined;
    }
    let cancelled = false;
    setReviewedUrl(undefined);
    proofApi
      .getProofById(proofId)
      .then((response) => {
        if (!cancelled) setReviewedUrl((response?.data as IProof | undefined)?.file ?? storedUrl);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        datadogLogs.logger.warn('List check reviewed document url refresh failed', {
          error,
          proof_id: proofId
        });
        setReviewedUrl(storedUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [proofId, storedUrl]);

  return reviewedUrl;
};

interface CandidateTileProps {
  entry: IListCheckEntry;
  candidate: IListCheckCandidate;
  position: number;
  currentProof?: IProof;
  isPick: boolean;
}

const CandidateTile: FC<CandidateTileProps> = ({
  entry,
  candidate,
  position,
  currentProof,
  isPick
}) => {
  const proofId = currentProof?.id;
  const checkKey = entry.check_key;
  const comparison = listCheckComparison(entry, candidate);
  const reviewedUrl = useReviewedUrl(currentProof);
  const isInspectorPanelEnabled = useFeatureFlag(FeatureFlagKey.ITL_2960_INSPECTOR_PANEL);

  const logError = useCallback(
    (error: unknown) => {
      datadogLogs.logger.error('List check compare render failed', {
        error,
        proof_id: proofId,
        check_key: checkKey
      });
    },
    [proofId, checkKey]
  );
  const rowId = candidate.row_ref ? (
    <ListCheckTileSub data-testid="list-check-tile-sub">
      <RowReference rowRef={candidate.row_ref} rowUrl={candidate.row_url} />
    </ListCheckTileSub>
  ) : null;

  return (
    <ListCheckTile
      $pick={isPick}
      data-testid={`list-check-candidate-${candidate.row_id ?? 'unknown'}`}
    >
      {isPick && (
        <ListCheckTileTop>
          <ListCheckPickTag>
            <svg
              width="13"
              height="13"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 6.4 4.6 9 10 3.2" />
            </svg>
            {listCheckCopy.yourPick}
          </ListCheckPickTag>
        </ListCheckTileTop>
      )}
      <ListCheckPanes>
        <DocumentPane
          containerId="list-check-pane-reviewed"
          title={listCheckCopy.documentUnderReview}
          url={reviewedUrl}
          fileName={cleanFileName(currentProof?.fileName ?? 'document')}
          fileId={proofId ?? 'none'}
          unavailableText="This document is not available to view."
          onError={logError}
        />
        <DocumentPane
          key={candidate.row_id ?? 'none'}
          containerId="list-check-pane-example"
          title={listCheckCopy.candidate(position)}
          titleAside={rowId}
          url={candidate.example_document_url}
          fileName={cleanFileName(candidate.example_document_name ?? 'example')}
          fileId={candidate.row_id ?? 'none'}
          unavailableText={listCheckCopy.noExampleDocument}
          downloadName={
            isInspectorPanelEnabled
              ? candidate.example_document_name ?? `candidate_${position}.pdf`
              : undefined
          }
          onError={logError}
        />
      </ListCheckPanes>
      {comparison && (
        <ComparisonTable
          comparison={comparison}
          position={position}
          isPositive={entry.match_is_positive === true}
        />
      )}
      <CandidateNotes candidate={candidate} />
    </ListCheckTile>
  );
};

export default CandidateTile;
